import { createFileRoute, Navigate, redirect } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import {
  INVOICE_ORDER_STATUSES,
  ORDER_PAYMENT_METHODS,
} from "@/features/admin/orders/invoiceOrders";
import { InvoiceOrdersPage } from "@/features/admin/orders/InvoiceOrdersPage";
import { fetchFeatureFlags } from "@/hooks/useFeatureFlags";
import { usePermissions } from "@/hooks/usePermissions";
import type { SeatOrderPaymentMethod, SeatOrderStatus } from "@/types/api";

export interface OrdersSearch {
  status?: SeatOrderStatus;
  /** Card or Invoice (LMS-193); undefined for both. */
  paymentMethod?: SeatOrderPaymentMethod;
}

export const Route = createFileRoute("/admin/orders")({
  validateSearch: (search: Record<string, unknown>): OrdersSearch => ({
    status: INVOICE_ORDER_STATUSES.includes(search.status as SeatOrderStatus)
      ? (search.status as SeatOrderStatus)
      : undefined,
    paymentMethod: ORDER_PAYMENT_METHODS.includes(search.paymentMethod as SeatOrderPaymentMethod)
      ? (search.paymentMethod as SeatOrderPaymentMethod)
      : undefined,
  }),
  // Seat orders are hidden while the payments or seat-orders flag is off (the API 404s them too).
  beforeLoad: async () => {
    const { payments, seatOrders } = await fetchFeatureFlags();
    if (!payments || !seatOrders) throw redirect({ to: "/admin" });
  },
  component: OrdersRoute,
});

/** The admin Orders list of invoice (LMS-151) and card (LMS-193) seat orders, for admins with Orders.Manage. */
function OrdersRoute() {
  const { status, paymentMethod } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { hasPermission } = usePermissions();

  return (
    // ProtectedRoute lets in anyone with the permission OR the role, and Orders.Manage can be taken off any role, so
    // the page checks it again.
    <ProtectedRoute requiredPermissions={["Orders.Manage"]} requiredRoles={["SuperAdmin"]}>
      {hasPermission("Orders.Manage") ? (
        <AdminPageLayout mainClassName="bg-secondary_alt">
          <InvoiceOrdersPage
            status={status}
            onStatusChange={(next) =>
              void navigate({ search: { status: next, paymentMethod }, replace: true })
            }
            paymentMethod={paymentMethod}
            onPaymentMethodChange={(next) =>
              void navigate({ search: { status, paymentMethod: next }, replace: true })
            }
          />
        </AdminPageLayout>
      ) : (
        <Navigate to="/admin" />
      )}
    </ProtectedRoute>
  );
}
